// Vendor-agnostic voice/concierge wrapper.
//
// Feature code calls `voice.startConversation(...)` and never imports the
// ElevenLabs SDK directly. To swap to Vapi/Retell/OpenAI Realtime, write
// a new adapter and re-export it here.

import { elevenLabsProvider } from './elevenlabs'
import type { ConciergeMode } from '../../config/concierge'

export type { ConciergeMode }

/** Intents the concierge can hand off to the page. */
export type BookingIntent =
  | { kind: 'open_booking'; serviceSlug: string }
  | { kind: 'contact_human' }
  | { kind: 'live_pricing'; serviceSlug: string }

export interface StartConversationInput {
  agentId: string
  mode: ConciergeMode
  signedUrlEndpoint: string
  /** Called when the agent invokes a client tool (open_booking, contact_human, get_live_pricing). */
  onIntent?: (intent: BookingIntent) => void
}

export interface ConversationHandle {
  end(): Promise<void>
  sessionId: string
}

export interface VoiceProvider {
  startConversation(input: StartConversationInput): Promise<ConversationHandle>
}

export const voice: VoiceProvider = elevenLabsProvider

// --- pure helpers exposed for tests + downstream wiring ---

interface ModeOverrides {
  textOnly: boolean
  allowText: boolean
  allowVoice: boolean
}

const MODE_TABLE: Record<ConciergeMode, ModeOverrides> = {
  voice_only: { textOnly: false, allowText: false, allowVoice: true },
  chat_only: { textOnly: true, allowText: true, allowVoice: false },
  multimodal: { textOnly: false, allowText: true, allowVoice: true },
}

export function modeToOverrides(mode: ConciergeMode): ModeOverrides {
  return MODE_TABLE[mode]
}

interface ToolCallPayload {
  tool?: string
  arguments?: { service_slug?: string }
}

/** Translate an ElevenLabs tool-call event into a typed BookingIntent (or null when unrecognised). */
export function parseIntent(payload: unknown): BookingIntent | null {
  if (!payload || typeof payload !== 'object') return null
  const p = payload as ToolCallPayload
  switch (p.tool) {
    case 'open_booking': {
      const slug = p.arguments?.service_slug?.trim()
      if (!slug) return null
      return { kind: 'open_booking', serviceSlug: slug }
    }
    case 'get_live_pricing': {
      const slug = p.arguments?.service_slug?.trim()
      if (!slug) return null
      return { kind: 'live_pricing', serviceSlug: slug }
    }
    case 'contact_human':
      return { kind: 'contact_human' }
    default:
      return null
  }
}

/** Fetch a short-lived signed URL from our backend. Throws on non-2xx. */
export async function fetchSignedUrl(endpoint: string, agentId: string): Promise<string> {
  const url = `${endpoint}?agent_id=${encodeURIComponent(agentId)}`
  const res = await fetch(url, { method: 'GET' })
  if (!res.ok) throw new Error(`signed-url fetch failed: ${res.status}`)
  const data = (await res.json()) as { signed_url?: string }
  if (!data.signed_url) throw new Error('signed-url response missing signed_url')
  return data.signed_url
}
